import {
  neuerEingangSchluessel,
  oeffneUmschlag,
  oeffneUmschlagRoh,
  versiegle,
  type EingangSchluessel,
} from '../crypto/inbox.ts';
import { newDataKey, sodiumReady } from '../crypto/vault.ts';
import { wrapDataKey } from '../crypto/session.ts';
import { api } from '../data/api.ts';
import { registerAttachment, uploadEncrypted } from '../data/attachments.ts';
import { store } from '../data/db.ts';
import { acquire, flushPersist, onDocsChanged, release } from '../data/docs.ts';
import { emptyNote, newId } from '../data/notes.ts';
import { markIndexDirty } from '../data/sync.ts';
import type { Auftrag, Einlieferung } from '../../../shared/protocol.ts';
import type { FundMeta, LocalNote } from '../data/types.ts';

/**
 * Fundstücke aus dem versiegelten Eingang (docs/KONZEPT.md, Abschnitt 20).
 *
 * Der Einlieferer legt fertige Stücke ab, versiegelt für den öffentlichen Schlüssel
 * des Vaults. Hier werden sie geöffnet und zu gewöhnlichen Notizen — mit allem, was
 * daran hängt: Verschlüsselung, Abgleich, Suche, Versionen, Papierkorb.
 *
 * Der Vorgang ist absichtlich in dieser Reihenfolge: erst die Zeile anlegen, dann
 * die Datei hochladen, **dann erst** quittieren. Bricht etwas dazwischen ab, liegt
 * das Stück noch im Eingang und der nächste Lauf holt es erneut. Umgekehrt — erst
 * quittieren — wäre ein verlorener Fund, und der fällt niemandem auf.
 */

export type Fund = LocalNote & { fund: FundMeta };

export function isFund(note: LocalNote): note is Fund {
  return note.fund != null;
}

/**
 * Ob an einem Fundstück etwas fehlt — das Transkript nämlich, fast immer.
 *
 * Zwei Wege, und der zweite ist der wichtigere: Neue Stücke tragen den Grund als
 * `hinweis` bei sich. Alles, was vor dieser Fassung hereinkam, hat das Feld nicht —
 * und genau dieser Altbestand ist der Grund, warum es das Nachholen überhaupt gibt.
 * Für ihn bleibt der Satz im Text der Beleg, den der Einlieferer damals schrieb.
 */
export function unvollstaendig(fund: Fund): boolean {
  if (typeof fund.fund.hinweis === 'string' && fund.fund.hinweis !== '') return true;
  if (fund.fund.hinweis === null) return false;
  return /ließ sich nicht laden|ohne Transkript|kein Transkript/i.test(fund.content.slice(0, 600));
}

/**
 * Zuletzt hinzugefügtes zuerst — nach dem Eingang, nicht nach dem Datum des Videos.
 *
 * Das Datum des Inhalts wäre die bibliothekarisch richtige Ordnung und ist trotzdem
 * die falsche: Wer etwas sucht, weiß, wann er es geschickt hat, nicht wann es
 * hochgeladen wurde. Ein TikTok von 2023, heute gespeichert, gehört nach oben.
 *
 * Nachgeholte Stücke behalten ihren ursprünglichen Eingang und springen deshalb
 * nicht an die Spitze, bloß weil das Transkript nachgereicht wurde.
 */
export function fundeOf(notes: LocalNote[]): Fund[] {
  return notes.filter(isFund).sort((a, b) => b.fund.eingang - a.fund.eingang);
}

/** Nur was hier steht, taugt als Herkunft — alles andere heißt „Datei". */
const QUELLEN = ['youtube', 'tiktok', 'instagram', 'website', 'sprachnachricht', 'datei'];

function saubereQuelle(wert: unknown): string {
  return typeof wert === 'string' && QUELLEN.includes(wert) ? wert : 'datei';
}

/**
 * Prüft, was aus dem Umschlag kommt, bevor es eine Notiz wird. Der Umschlag ist zwar
 * echt — nur wer den Einlieferungsschlüssel hat, kann ihn ablegen — aber „echt" heißt
 * nicht „wohlgeformt": Ein Fehler im Einlieferer darf hier keine kaputte Zeile bauen.
 */
function pruefe(roh: unknown): Einlieferung | null {
  if (typeof roh !== 'object' || roh === null) return null;
  const wert = roh as Record<string, unknown>;
  if (typeof wert.titel !== 'string' || typeof wert.text !== 'string') return null;
  const datei =
    typeof wert.datei === 'object' && wert.datei !== null
      ? (wert.datei as Record<string, unknown>)
      : null;
  return {
    version: 1,
    quelle: saubereQuelle(wert.quelle),
    titel: wert.titel.slice(0, 300).trim() || 'Ohne Titel',
    text: wert.text,
    url: typeof wert.url === 'string' ? wert.url : null,
    autor: typeof wert.autor === 'string' ? wert.autor : null,
    datum: typeof wert.datum === 'number' && Number.isFinite(wert.datum) ? wert.datum : null,
    hinweis: typeof wert.hinweis === 'string' && wert.hinweis !== '' ? wert.hinweis.slice(0, 300) : null,
    // Eine Notiz-ID und nichts anderes: Der Umschlag ist echt, aber er soll auch nicht
    // versehentlich auf etwas zeigen können, das keine Notiz ist.
    ersetzt: typeof wert.ersetzt === 'string' && /^[\w-]{1,64}$/.test(wert.ersetzt) ? wert.ersetzt : null,
    vorschau:
      typeof wert.vorschau === 'string' && wert.vorschau.startsWith('data:image/')
        ? wert.vorschau
        : null,
    datei:
      datei && typeof datei.name === 'string'
        ? {
            name: String(datei.name).slice(0, 200),
            mime: typeof datei.mime === 'string' ? datei.mime : 'application/octet-stream',
            bytes: typeof datei.bytes === 'number' ? datei.bytes : 0,
          }
        : null,
  };
}

/** Der Schlüssel des Eingangs liegt im Vault-Index — dort ist er verschlüsselt und wandert mit. */
async function ausIndex(): Promise<EingangSchluessel | null> {
  const index = await store.getIndex();
  return index.eingang ?? null;
}

async function inIndex(schluessel: EingangSchluessel): Promise<void> {
  const index = await store.getIndex();
  await store.setIndex({ ...index, eingang: schluessel });
  markIndexDirty();
}

export type Lage = {
  /** Ist der Eingang überhaupt eingerichtet? */
  bereit: boolean;
  /** Der öffentliche Schlüssel, wie ihn der Server kennt. */
  publicKey: string | null;
  /** Kennt der Server einen anderen als dieses Gerät? Dann ist etwas durcheinander. */
  abweichung: boolean;
};

export const funde = {
  /**
   * Zustand des Eingangs. Die Abweichung ist der wichtige Teil: Wurde auf einem
   * anderen Gerät ein neuer Schlüssel erzeugt, kann dieses Gerät nichts mehr öffnen —
   * das muss man sehen können, statt es an leeren Abholungen zu erraten.
   */
  async lage(): Promise<Lage> {
    const lokal = await ausIndex();
    const entfernt = await api.inboxKey().catch(() => ({ publicKey: null }));
    return {
      bereit: lokal !== null && entfernt.publicKey === lokal.publicKey,
      publicKey: entfernt.publicKey,
      abweichung: lokal !== null && entfernt.publicKey !== null && entfernt.publicKey !== lokal.publicKey,
    };
  },

  /**
   * Richtet den Eingang ein: Schlüsselpaar erzeugen, den privaten Teil in den Vault,
   * den öffentlichen zum Server. Ein zweiter Aufruf erzeugt bewusst ein neues Paar —
   * das ist der Weg, einen verlorenen Schlüssel loszuwerden, und der Server wirft
   * dabei weg, was noch unabholbar im Eingang liegt.
   */
  async einrichten(): Promise<string> {
    const schluessel = await neuerEingangSchluessel();
    await inIndex(schluessel);
    await api.setInboxKey(schluessel.publicKey);
    return schluessel.publicKey;
  },

  /** Legt einen Einlieferungsschlüssel an. Der Klartext ist danach nirgends mehr zu haben. */
  neuerAbsender: (name: string) => api.newInboxSender(name),
  absender: () => api.inboxSenders(),
  absenderLoeschen: (id: string) => api.deleteInboxSender(id),

  /**
   * Holt ab, was da ist. Gibt zurück, wie viele Stücke Notizen wurden — und wie viele
   * sich nicht öffnen ließen, denn das heißt fast immer: falscher Schlüssel.
   */
  async abholen(
    onProgress?: (fertig: number, gesamt: number) => void,
  ): Promise<{ neu: number; unlesbar: number }> {
    const schluessel = await ausIndex();
    if (!schluessel) return { neu: 0, unlesbar: 0 };

    const { items } = await api.inbox();
    let neu = 0;
    let unlesbar = 0;

    for (const [nummer, item] of items.entries()) {
      onProgress?.(nummer, items.length);
      let inhalt: Einlieferung | null = null;
      try {
        const klar = oeffneUmschlag(item.sealed, schluessel);
        inhalt = pruefe(JSON.parse(new TextDecoder().decode(klar)));
      } catch {
        // Nicht zu öffnen heißt: für einen anderen Schlüssel versiegelt. Liegen
        // lassen — ein Gerät mit dem richtigen Schlüssel kann es noch abholen.
        unlesbar += 1;
        continue;
      }
      if (!inhalt) {
        unlesbar += 1;
        continue;
      }

      try {
        await anlegen(item.id, inhalt, item.hatDatei, schluessel);
        neu += 1;
      } catch (error) {
        // Ein einzelnes Stück hält den Rest nicht auf; unquittiert bleibt es liegen
        // und wird beim nächsten Lauf erneut versucht.
        console.warn('[eingang] Stück konnte nicht übernommen werden:', error);
      }
    }
    onProgress?.(items.length, items.length);
    return { neu, unlesbar };
  },

  /**
   * Bittet den Einlieferer, eine Adresse noch einmal zu holen (Abschnitt 20.5).
   *
   * Der Auftrag wird für den Einlieferer versiegelt, nicht für den Vault: Er muss die
   * Adresse lesen können, der Server nicht. Kommt das Ergebnis zurück, trägt es die
   * Notiz-Kennung bei sich und ersetzt den Torso, statt daneben ein zweites Stück
   * anzulegen — sonst hätte man am Ende von jedem Video zwei halbe Notizen.
   */
  async beauftragen(stuecke: Fund[]): Promise<{ beauftragt: number; ohneAdresse: number }> {
    const mitAdresse = stuecke.filter((stueck) => (stueck.fund.url ?? '') !== '');
    if (mitAdresse.length === 0) {
      return { beauftragt: 0, ohneAdresse: stuecke.length };
    }

    const { arbeiter } = await api.arbeiter();
    const nimmt = arbeiter[0];
    if (!nimmt) throw new KeinEinlieferer();

    await sodiumReady();
    let beauftragt = 0;
    for (const stueck of mitAdresse) {
      const auftrag: Auftrag = { version: 1, url: stueck.fund.url ?? '', notiz: stueck.id };
      const sealed = versiegle(new TextEncoder().encode(JSON.stringify(auftrag)), nimmt.jobKey);
      await api.neuerAuftrag(nimmt.id, sealed);
      beauftragt += 1;
    }
    return { beauftragt, ohneAdresse: stuecke.length - mitAdresse.length };
  },

  /** Wie viel gerade beim Einlieferer liegt. */
  auftraege: () => api.auftraege(),

  onChange(listener: () => void): () => void {
    return onDocsChanged(() => listener());
  },
};

/** Niemand da, der Aufträge annimmt — der Einlieferer läuft nicht oder ist zu alt. */
export class KeinEinlieferer extends Error {
  constructor() {
    super('Kein Einlieferer meldet sich für Aufträge.');
  }
}

/** Aus einem geöffneten Umschlag wird eine Notiz — und erst danach wird quittiert. */
async function anlegen(
  itemId: string,
  inhalt: Einlieferung,
  hatDatei: boolean,
  schluessel: EingangSchluessel,
): Promise<void> {
  const now = Date.now();
  // Antwort auf einen Auftrag: Dann wird der Torso überschrieben statt verdoppelt.
  // Gibt es die Notiz nicht mehr — gelöscht, auf diesem Gerät noch nie gesehen —,
  // entsteht eine neue. Ein Fund, der wegen einer fehlenden Zeile verschwindet,
  // wäre der schlechtere Ausgang.
  //
  // Und nur ein Fundstück: Die Kennung kommt zwar aus einem Auftrag, den diese App
  // selbst versiegelt hat, aber ein Einlieferer, dem jemand den Schlüssel abgenommen
  // hat, könnte hier sonst jede beliebige Notiz überschreiben. Ein Fundstück zu
  // überschreiben ist der Zweck der Übung; alles andere ist keiner.
  const ersetzt = inhalt.ersetzt ? await store.getNote(inhalt.ersetzt) : undefined;
  const ersetzen = ersetzt !== undefined && ersetzt.deletedAt === null && ersetzt.fund != null;
  const id = ersetzen ? (inhalt.ersetzt as string) : newId();

  if (!ersetzen) {
    await store.putNote({ ...emptyNote(id, wrapDataKey(newDataKey()), now), dirty: true });
  }

  const meta: FundMeta = {
    eingang: now,
    quelle: inhalt.quelle,
    url: inhalt.url,
    autor: inhalt.autor,
    datum: inhalt.datum,
    vorschau: inhalt.vorschau,
    hinweis: inhalt.hinweis ?? null,
  };

  const entry = await acquire(id);
  entry.doc.transact(() => {
    if (ersetzen) {
      // Der alte Stand ist damit nicht verloren: Die Versionen der Notiz halten ihn.
      const alt = entry.meta.get('fund') as FundMeta | undefined;
      entry.text.delete(0, entry.text.length);
      entry.meta.set('fund', { ...meta, eingang: alt?.eingang ?? now, erneuert: now });
    } else {
      entry.meta.set('createdAt', now);
      entry.meta.set('pinned', false);
      entry.meta.set('folderId', null);
      entry.meta.set('fund', meta);
    }
    entry.text.insert(0, `# ${inhalt.titel}\n\n${inhalt.text}\n`);
  });
  release(id);
  await flushPersist(id);

  if (hatDatei && inhalt.datei) {
    const versiegelt = await api.inboxFile(itemId);
    const bytes = oeffneUmschlagRoh(versiegelt, schluessel);
    const uploaded = await uploadEncrypted(bytes, inhalt.datei.mime, inhalt.datei.name, id);
    await registerAttachment(id, uploaded.id, uploaded.meta);
  }

  // Erst jetzt: Was hier steht, steht auch wirklich.
  await api.ackInbox(itemId);
}
